import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar as CalendarIcon, ArrowLeft, Crown, Target, Upload, Sparkles, X, FileImage, Globe, Repeat, Eye, EyeOff, Users } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { format, parse, addWeeks, addMonths } from 'date-fns';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreateEvent = () => {
  const navigate = useNavigate();
  const { user, token, loading: authLoading, isBusinessPremium } = useAuth();
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [jobFunctions, setJobFunctions] = useState([]);
  const [companyTypes, setCompanyTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzingFlyer, setAnalyzingFlyer] = useState(false);
  const [flyerPreview, setFlyerPreview] = useState(null);
  const fileInputRef = useRef(null);
  const [date, setDate] = useState(undefined);
  const [isPaid, setIsPaid] = useState(false);
  const [isPublic, setIsPublic] = useState(true);
  const [rsvpEnabled, setRsvpEnabled] = useState(true);
  const [isInviteOnly, setIsInviteOnly] = useState(false);
  const [showTargeting, setShowTargeting] = useState(false);
  const [showRecurrence, setShowRecurrence] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    region: 'nyc',
    start_time: '',
    end_time: '',
    location: '',
    address: '',
    capacity: '',
    max_rsvp: '',
    registration_link: '',
    image_url: '',
    ticket_price: '',
  });
  const [targeting, setTargeting] = useState({
    include_job_functions: [],
    include_company_types: [],
    exclude_company_types: [],
  });
  const [recurrence, setRecurrence] = useState({
    type: 'none',
    interval: 1,
    end_date: '',
    occurrences: '',
  });
  const [endDate, setEndDate] = useState(undefined);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchReferenceData();
  }, [user, navigate, authLoading]);

  const fetchReferenceData = async () => {
    try {
      const [catsRes, regionsRes, jobsRes, typesRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/regions`),
        axios.get(`${API}/job-functions`),
        axios.get(`${API}/company-types`),
      ]);
      setCategories(catsRes.data);
      setRegions(regionsRes.data);
      setJobFunctions(jobsRes.data);
      setCompanyTypes(typesRes.data);
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  const handleFlyerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WebP image');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setFlyerPreview(e.target.result);
    reader.readAsDataURL(file);

    // Analyze with AI
    setAnalyzingFlyer(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/events/analyze-flyer`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success && response.data.extracted_data) {
        const data = response.data.extracted_data;
        
        // Populate form with extracted data
        setFormData(prev => ({
          ...prev,
          title: data.title || prev.title,
          description: data.description || prev.description,
          category: data.category || prev.category,
          start_time: data.start_time || prev.start_time,
          end_time: data.end_time || prev.end_time,
          location: data.location || prev.location,
          address: data.address || prev.address,
          ticket_price: data.ticket_price ? String(data.ticket_price) : prev.ticket_price,
        }));

        // Set date if extracted
        if (data.date) {
          try {
            const parsedDate = parse(data.date, 'yyyy-MM-dd', new Date());
            setDate(parsedDate);
          } catch (e) {
            console.error('Could not parse date:', data.date);
          }
        }

        // Set paid event if price > 0
        if (data.ticket_price && data.ticket_price > 0) {
          setIsPaid(true);
        }

        toast.success(`Event details extracted! Confidence: ${data.confidence || 'medium'}`);
      } else {
        toast.error('Could not extract event details. Please fill in manually.');
      }
    } catch (error) {
      console.error('Flyer analysis error:', error);
      toast.error(error.response?.data?.detail || 'Error analyzing flyer');
    } finally {
      setAnalyzingFlyer(false);
    }
  };

  const clearFlyer = () => {
    setFlyerPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTargetingChange = (field, value, checked) => {
    setTargeting(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter(v => v !== value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error('Please select a date');
      return;
    }

    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }

    if (isPaid && (!formData.ticket_price || parseFloat(formData.ticket_price) <= 0)) {
      toast.error('Please enter a valid ticket price');
      return;
    }

    // Check if targeting is used without premium
    const hasTargeting = targeting.include_job_functions.length > 0 ||
                        targeting.include_company_types.length > 0 ||
                        targeting.exclude_company_types.length > 0;
    
    if (hasTargeting && !isBusinessPremium()) {
      toast.error('Audience targeting requires Business Premium membership');
      return;
    }

    // Build recurrence object if enabled
    let recurrenceData = null;
    if (showRecurrence && recurrence.type !== 'none') {
      recurrenceData = {
        type: recurrence.type,
        interval: parseInt(recurrence.interval) || 1,
        end_date: endDate ? format(endDate, 'yyyy-MM-dd') : null,
        occurrences: recurrence.occurrences ? parseInt(recurrence.occurrences) : null,
      };
    }

    setLoading(true);
    try {
      const eventData = {
        ...formData,
        date: format(date, 'yyyy-MM-dd'),
        capacity: formData.capacity ? parseInt(formData.capacity) : null,
        max_rsvp: formData.max_rsvp ? parseInt(formData.max_rsvp) : null,
        is_paid: isPaid,
        ticket_price: isPaid ? parseFloat(formData.ticket_price) : null,
        is_public: isPublic,
        rsvp_enabled: rsvpEnabled,
        is_invite_only: isInviteOnly,
        audience_targeting: hasTargeting ? targeting : null,
        recurrence: recurrenceData,
      };

      const response = await axios.post(`${API}/events`, eventData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (recurrenceData) {
        toast.success('Recurring event series created!');
      } else if (isInviteOnly) {
        toast.success('Invite-only event created! You can now send invitations.');
      } else {
        toast.success('Event created successfully!');
      }
      navigate(`/events/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating event');
    } finally {
      setLoading(false);
    }
  };

  // Calculate preview of recurring dates
  const getRecurrencePreview = () => {
    if (!date || !showRecurrence || recurrence.type === 'none') return [];
    
    const dates = [date];
    let current = date;
    const interval = parseInt(recurrence.interval) || 1;
    const maxPreview = 5;
    
    for (let i = 1; i < maxPreview; i++) {
      if (recurrence.type === 'weekly') {
        current = addWeeks(current, interval);
      } else if (recurrence.type === 'monthly') {
        current = addMonths(current, interval);
      }
      
      if (endDate && current > endDate) break;
      dates.push(current);
    }
    
    return dates;
  };

  return (
    <div className="min-h-screen py-12">
      <div className="container-custom max-w-3xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-gold mb-8"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mb-10">
          <p className="overline text-gold mb-4">New Event</p>
          <h1 className="text-3xl md:text-4xl">Create Event</h1>
        </div>

        {/* Flyer Upload Section */}
        <div className="mb-10 border-2 border-dashed border-gold/50 rounded-lg p-6 bg-gold/5">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-6 h-6 text-gold" />
            <div>
              <h3 className="font-semibold">Upload Event Flyer</h3>
              <p className="text-sm text-muted-foreground">
                Upload a flyer and we'll automatically extract the event details using AI
              </p>
            </div>
          </div>

          {flyerPreview ? (
            <div className="relative">
              <img 
                src={flyerPreview} 
                alt="Flyer preview" 
                className="max-h-64 mx-auto rounded-lg object-contain"
              />
              <button
                onClick={clearFlyer}
                className="absolute top-2 right-2 p-1 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90"
                data-testid="clear-flyer-btn"
              >
                <X className="w-4 h-4" />
              </button>
              {analyzingFlyer && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                  <div className="text-center text-white">
                    <div className="spinner mx-auto mb-2" />
                    <p className="text-sm">Analyzing flyer...</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div 
              className="border border-border/50 rounded-lg p-8 text-center cursor-pointer hover:border-gold transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <FileImage className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-2">
                Click to upload or drag and drop
              </p>
              <p className="text-xs text-muted-foreground">
                JPEG, PNG, or WebP (max 10MB)
              </p>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFlyerUpload}
            className="hidden"
            data-testid="flyer-upload-input"
          />

          {!flyerPreview && (
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={analyzingFlyer}
              className="w-full mt-4 border-gold text-gold hover:bg-gold/10"
              data-testid="upload-flyer-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {analyzingFlyer ? 'Analyzing...' : 'Upload Flyer'}
            </Button>
          )}
        </div>

        <div className="relative mb-10">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border/50" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-background px-4 text-sm text-muted-foreground">
              Or fill in manually
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-sm uppercase tracking-wider">
              Event Title *
            </Label>
            <Input
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Aging Care Leadership Summit 2025"
              required
              className="rounded-sm border-border/50"
              data-testid="event-title-input"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm uppercase tracking-wider">
              Description *
            </Label>
            <Textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe your event, what attendees can expect, topics covered..."
              required
              rows={6}
              className="rounded-sm border-border/50 resize-none"
              data-testid="event-description-input"
            />
          </div>

          {/* Category and Date Row */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-sm uppercase tracking-wider">Category *</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
                <SelectTrigger className="rounded-sm border-border/50" data-testid="category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm uppercase tracking-wider flex items-center gap-2">
                <Globe className="w-4 h-4 text-gold" />
                Region *
              </Label>
              <Select value={formData.region} onValueChange={(value) => setFormData(prev => ({ ...prev, region: value }))}>
                <SelectTrigger className="rounded-sm border-border/50" data-testid="region-select">
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent>
                  {regions.map((region) => (
                    <SelectItem key={region.value} value={region.value}>
                      {region.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Date Row */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-sm uppercase tracking-wider">Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal rounded-sm border-border/50"
                    data-testid="date-picker-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, 'PPP') : <span className="text-muted-foreground">Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Time Row */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="start_time" className="text-sm uppercase tracking-wider">
                Start Time *
              </Label>
              <Input
                id="start_time"
                name="start_time"
                type="time"
                value={formData.start_time}
                onChange={handleChange}
                required
                className="rounded-sm border-border/50"
                data-testid="start-time-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="end_time" className="text-sm uppercase tracking-wider">
                End Time *
              </Label>
              <Input
                id="end_time"
                name="end_time"
                type="time"
                value={formData.end_time}
                onChange={handleChange}
                required
                className="rounded-sm border-border/50"
                data-testid="end-time-input"
              />
            </div>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location" className="text-sm uppercase tracking-wider">
              Venue Name *
            </Label>
            <Input
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="e.g., The Plaza Hotel"
              required
              className="rounded-sm border-border/50"
              data-testid="location-input"
            />
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label htmlFor="address" className="text-sm uppercase tracking-wider">
              Full Address *
            </Label>
            <Input
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="e.g., 768 5th Ave, New York, NY 10019"
              required
              className="rounded-sm border-border/50"
              data-testid="address-input"
            />
          </div>

          {/* Event Visibility & RSVP Settings */}
          <div className="border border-border/50 p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-wider font-medium mb-4">Event Settings</h3>
            
            {/* Public/Private Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm uppercase tracking-wider">Public Event</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {isPublic ? 'Event is visible to everyone' : 'Only you can see this event'}
                </p>
              </div>
              <Switch
                checked={isPublic}
                onCheckedChange={setIsPublic}
                data-testid="public-event-toggle"
              />
            </div>

            {/* RSVP Enabled Toggle */}
            <div className="flex items-center justify-between pt-4 border-t border-border/50">
              <div>
                <Label className="text-sm uppercase tracking-wider">Enable RSVP</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {rsvpEnabled ? 'Attendees can register for this event' : 'RSVP is disabled'}
                </p>
              </div>
              <Switch
                checked={rsvpEnabled}
                onCheckedChange={setRsvpEnabled}
                data-testid="rsvp-enabled-toggle"
              />
            </div>

            {/* Max RSVP Limit */}
            {rsvpEnabled && !isPaid && (
              <div className="space-y-2 pt-4 border-t border-border/50">
                <Label htmlFor="max_rsvp" className="text-sm uppercase tracking-wider">
                  Maximum RSVPs (Optional)
                </Label>
                <Input
                  id="max_rsvp"
                  name="max_rsvp"
                  type="number"
                  min="1"
                  value={formData.max_rsvp}
                  onChange={handleChange}
                  placeholder="Leave empty for unlimited"
                  className="rounded-sm border-border/50"
                  data-testid="max-rsvp-input"
                />
                <p className="text-xs text-muted-foreground">
                  Limit the number of RSVPs. Use venue capacity for overall attendance limit.
                </p>
              </div>
            )}

            {/* Invite Only Toggle */}
            {rsvpEnabled && !isPaid && (
              <div className="flex items-center justify-between pt-4 border-t border-border/50">
                <div>
                  <Label className="text-sm uppercase tracking-wider">Invite Only</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {isInviteOnly ? 'Only invited guests can RSVP' : 'Anyone can RSVP to this event'}
                  </p>
                </div>
                <Switch
                  checked={isInviteOnly}
                  onCheckedChange={setIsInviteOnly}
                  data-testid="invite-only-toggle"
                />
              </div>
            )}
            {isInviteOnly && rsvpEnabled && !isPaid && (
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-sm">
                <p className="text-sm text-blue-400">
                  After creating this event, you'll be able to send invitation links to specific people from the event page.
                </p>
              </div>
            )}
          </div>

          {/* Paid Event Toggle */}
          <div className="border border-border/50 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm uppercase tracking-wider">Paid Event</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Charge attendees for tickets via Stripe
                </p>
              </div>
              <Switch
                checked={isPaid}
                onCheckedChange={setIsPaid}
                data-testid="paid-event-toggle"
              />
            </div>
            
            {isPaid && (
              <div className="space-y-2 pt-4 border-t border-border/50">
                <Label htmlFor="ticket_price" className="text-sm uppercase tracking-wider">
                  Ticket Price (USD) *
                </Label>
                <Input
                  id="ticket_price"
                  name="ticket_price"
                  type="number"
                  step="0.01"
                  min="1"
                  value={formData.ticket_price}
                  onChange={handleChange}
                  placeholder="e.g., 50.00"
                  className="rounded-sm border-border/50"
                  data-testid="ticket-price-input"
                />
              </div>
            )}

            {/* Platform Commission Notice for Paid Events */}
            {isPaid && formData.ticket_price && (
              <div className="p-4 bg-gold/10 border border-gold/30 rounded-sm mt-4">
                <p className="text-sm font-medium mb-2">Earnings Breakdown</p>
                <div className="text-sm text-muted-foreground space-y-1">
                  <div className="flex justify-between">
                    <span>Ticket Price:</span>
                    <span>${parseFloat(formData.ticket_price).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gold">
                    <span>Platform Fee ({isBusinessPremium() ? '15%' : '25%'}):</span>
                    <span>-${(parseFloat(formData.ticket_price) * (isBusinessPremium() ? 0.15 : 0.25)).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-medium pt-1 border-t border-border/50">
                    <span>Your Earnings:</span>
                    <span>${(parseFloat(formData.ticket_price) * (isBusinessPremium() ? 0.85 : 0.75)).toFixed(2)}</span>
                  </div>
                </div>
                {isBusinessPremium() ? (
                  <p className="text-xs text-green-500 mt-2">
                    ✓ Business Premium discount applied! You're saving 10% on platform fees.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground mt-2">
                    Upgrade to Business Premium for a reduced 15% platform fee (vs 25% standard).
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Recurring Event */}
          <div className="border border-border/50 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Repeat className="w-5 h-5 text-gold" />
                <div>
                  <Label className="text-sm uppercase tracking-wider">Recurring Event</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Create a series of repeating events
                  </p>
                </div>
              </div>
              <Switch
                checked={showRecurrence}
                onCheckedChange={setShowRecurrence}
                data-testid="recurrence-toggle"
              />
            </div>

            {showRecurrence && (
              <div className="space-y-4 pt-4 border-t border-border/50">
                {/* Recurrence Type */}
                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider">Repeat</Label>
                  <Select 
                    value={recurrence.type} 
                    onValueChange={(value) => setRecurrence(prev => ({ ...prev, type: value }))}
                  >
                    <SelectTrigger className="rounded-sm border-border/50" data-testid="recurrence-type">
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Does not repeat</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {recurrence.type !== 'none' && (
                  <>
                    {/* Interval */}
                    <div className="space-y-2">
                      <Label className="text-sm uppercase tracking-wider">
                        Every {recurrence.type === 'weekly' ? 'Week(s)' : 'Month(s)'}
                      </Label>
                      <Select 
                        value={String(recurrence.interval)} 
                        onValueChange={(value) => setRecurrence(prev => ({ ...prev, interval: parseInt(value) }))}
                      >
                        <SelectTrigger className="rounded-sm border-border/50" data-testid="recurrence-interval">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4].map(n => (
                            <SelectItem key={n} value={String(n)}>
                              Every {n} {recurrence.type === 'weekly' ? (n === 1 ? 'week' : 'weeks') : (n === 1 ? 'month' : 'months')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* End Date or Occurrences */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm uppercase tracking-wider">End Date (optional)</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left font-normal rounded-sm border-border/50"
                              data-testid="recurrence-end-date"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {endDate ? format(endDate, 'PPP') : <span className="text-muted-foreground">Pick end date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={endDate}
                              onSelect={setEndDate}
                              disabled={(d) => d < (date || new Date())}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm uppercase tracking-wider">Or # of Events</Label>
                        <Input
                          type="number"
                          min="2"
                          max="52"
                          value={recurrence.occurrences}
                          onChange={(e) => setRecurrence(prev => ({ ...prev, occurrences: e.target.value }))}
                          placeholder="e.g., 10"
                          className="rounded-sm border-border/50"
                          data-testid="recurrence-occurrences"
                        />
                      </div>
                    </div>

                    {/* Preview */}
                    {date && (
                      <div className="p-3 bg-secondary/50 rounded-sm">
                        <p className="text-sm font-medium mb-2">Preview (first 5 dates):</p>
                        <div className="flex flex-wrap gap-2">
                          {getRecurrencePreview().map((d, i) => (
                            <span key={i} className="text-xs px-2 py-1 bg-gold/20 text-gold rounded">
                              {format(d, 'MMM d, yyyy')}
                            </span>
                          ))}
                          <span className="text-xs text-muted-foreground">...</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Audience Targeting (Premium) */}
          <div className="border border-border/50 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-gold" />
                <div>
                  <Label className="text-sm uppercase tracking-wider">Audience Targeting</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Restrict who can see and register for this event
                  </p>
                </div>
              </div>
              {isBusinessPremium() ? (
                <Switch
                  checked={showTargeting}
                  onCheckedChange={setShowTargeting}
                  data-testid="targeting-toggle"
                />
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/membership')}
                  className="text-gold border-gold"
                >
                  <Crown className="w-4 h-4 mr-1" /> Upgrade
                </Button>
              )}
            </div>

            {showTargeting && isBusinessPremium() && (
              <div className="space-y-6 pt-4 border-t border-border/50">
                {/* Include Job Functions */}
                <div>
                  <Label className="text-sm uppercase tracking-wider mb-3 block">
                    Include Job Functions (leave empty for all)
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    {jobFunctions.map((job) => (
                      <div key={job.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={`include-job-${job.value}`}
                          checked={targeting.include_job_functions.includes(job.value)}
                          onCheckedChange={(checked) => handleTargetingChange('include_job_functions', job.value, checked)}
                        />
                        <Label htmlFor={`include-job-${job.value}`} className="text-sm cursor-pointer">
                          {job.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Include Company Types */}
                <div>
                  <Label className="text-sm uppercase tracking-wider mb-3 block">
                    Include Company Types (leave empty for all)
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    {companyTypes.map((type) => (
                      <div key={type.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={`include-type-${type.value}`}
                          checked={targeting.include_company_types.includes(type.value)}
                          onCheckedChange={(checked) => handleTargetingChange('include_company_types', type.value, checked)}
                        />
                        <Label htmlFor={`include-type-${type.value}`} className="text-sm cursor-pointer">
                          {type.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Exclude Company Types */}
                <div>
                  <Label className="text-sm uppercase tracking-wider mb-3 block text-destructive">
                    Exclude Company Types
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    {companyTypes.map((type) => (
                      <div key={type.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={`exclude-type-${type.value}`}
                          checked={targeting.exclude_company_types.includes(type.value)}
                          onCheckedChange={(checked) => handleTargetingChange('exclude_company_types', type.value, checked)}
                        />
                        <Label htmlFor={`exclude-type-${type.value}`} className="text-sm cursor-pointer">
                          {type.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Optional Fields */}
          <div className="border-t border-border/50 pt-8">
            <p className="overline text-muted-foreground mb-6">Optional Details</p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="capacity" className="text-sm uppercase tracking-wider">
                  Capacity
                </Label>
                <Input
                  id="capacity"
                  name="capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={handleChange}
                  placeholder="e.g., 100"
                  min="1"
                  className="rounded-sm border-border/50"
                  data-testid="capacity-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="registration_link" className="text-sm uppercase tracking-wider">
                  External Registration Link
                </Label>
                <Input
                  id="registration_link"
                  name="registration_link"
                  type="url"
                  value={formData.registration_link}
                  onChange={handleChange}
                  placeholder="https://..."
                  className="rounded-sm border-border/50"
                  data-testid="registration-link-input"
                />
              </div>
            </div>

            <div className="space-y-2 mt-6">
              <Label htmlFor="image_url" className="text-sm uppercase tracking-wider">
                Event Image URL
              </Label>
              <Input
                id="image_url"
                name="image_url"
                type="url"
                value={formData.image_url}
                onChange={handleChange}
                placeholder="https://..."
                className="rounded-sm border-border/50"
                data-testid="image-url-input"
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to use default event image
              </p>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4 pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="flex-1 rounded-sm uppercase tracking-widest"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gold text-primary hover:bg-gold/90 rounded-sm uppercase tracking-widest"
              data-testid="create-event-submit-btn"
            >
              {loading ? <span className="spinner mr-2" /> : null}
              Create Event
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEvent;
